import { useEffect, useMemo, useState } from "react";
import { Button, ConfirmModal, LabInvoicePdfFrame, showError, showSuccess } from "@common";
import {
  useClinics,
  useDeleteInvisalignInvoice,
  useInvisalignInvoiceClinicOverview,
  useInvisalignInvoiceReview,
  useInvisalignInvoiceViewUrl,
} from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { formatMoney } from "@utils/format";

/**
 * Admin view of every Invisalign invoice PDF (apps/invisalign_invoices), the
 * counterpart of LabInvoiceReviewPanel. Invoices are uploaded per clinic-day on
 * Daily Financial Reporting and read by plain code on upload; this panel only
 * browses, previews and deletes them. A blank `review_reason` means the invoice
 * was read and matched to one practitioner, so its amount is offered as
 * "Suggested:" under Invisalign Costs.
 */

const PAGE_SIZE = 20;

const DOCUMENT_TYPE_LABELS = {
  invoice: "Invoice",
  credit_note: "Credit note",
};

function OverviewStat({ label, value, tone = "slate" }) {
  const toneClass =
    tone === "amber"
      ? "border-palette-amber-border bg-palette-amber-soft text-palette-amber-text"
      : tone === "yellow"
        ? "border-palette-yellow-border bg-palette-yellow-soft text-palette-yellow-text"
        : tone === "rose"
          ? "border-palette-rose-border bg-palette-rose-soft text-palette-rose-heading"
          : tone === "emerald"
            ? "border-palette-emerald-border bg-palette-emerald-soft text-palette-emerald-hover"
            : "border-palette-slate-divider bg-white text-palette-slate-heading";

  return (
    <div className={`rounded-md border px-3 py-2 ${toneClass}`}>
      <div className="text-xs font-medium opacity-80">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value ?? 0}</div>
    </div>
  );
}

function MonthFilter({ month, onMonthChange }) {
  return (
    <label className="text-xs font-medium text-palette-slate-badge">
      Invoice month
      <input
        type="month"
        className="mt-1 block w-44 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
        value={month}
        onChange={(event) => onMonthChange(event.target.value)}
      />
    </label>
  );
}

function ClinicOverviewTab({
  data,
  isLoading,
  isError,
  error,
  onReviewClinic,
  month,
  onMonthChange,
}) {
  const rows = data?.results || [];
  const totals = data?.totals || {};

  const monthFilter = (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
      <MonthFilter month={month} onMonthChange={onMonthChange} />
      {month ? (
        <Button type="button" variant="secondary" size="sm" onClick={() => onMonthChange("")}>
          All months
        </Button>
      ) : (
        <span className="pb-1.5 text-xs text-palette-slate-secondary">Showing all months</span>
      )}
    </div>
  );

  if (isLoading || isError) {
    return (
      <div className="space-y-4">
        {monthFilter}
        {isLoading ? (
          <div className="text-sm text-palette-slate-secondary">Loading clinic overview...</div>
        ) : (
          <div className="text-sm text-palette-rose-badge">
            {getApiErrorMessage(error, "Unable to load Invisalign invoice clinic overview.")}
          </div>
        )}
      </div>
    );
  }

  const gridCols =
    "grid-cols-[minmax(160px,1.2fr)_repeat(4,minmax(72px,0.5fr))_repeat(2,minmax(100px,0.7fr))_auto]";

  return (
    <div className="space-y-4">
      {monthFilter}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <OverviewStat label="Total PDFs" value={totals.total_pdfs} />
        <OverviewStat label="Matched" value={totals.matched} tone="emerald" />
        <OverviewStat label="Service charges" value={totals.service_charges} tone="yellow" />
        <OverviewStat label="Needs review" value={totals.needs_review} tone="rose" />
        <OverviewStat label="Total amount" value={formatMoney(totals.amount_total ?? 0)} />
        <OverviewStat
          label="Suggested amount"
          value={formatMoney(totals.suggested_total ?? 0)}
          tone="emerald"
        />
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-palette-slate-secondary">
          {month ? "No Invisalign invoices dated in this month." : "No Invisalign invoices found."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-palette-slate-divider bg-white">
          <div
            className={`grid ${gridCols} gap-2 border-b border-palette-slate-divider bg-palette-slate-soft px-3 py-2 text-xs font-semibold uppercase text-palette-slate-secondary`}
          >
            <div>Clinic</div>
            <div className="text-right">PDFs</div>
            <div className="text-right">Credits</div>
            <div className="text-right">Matched</div>
            <div className="text-right">Review</div>
            <div className="text-right">Total</div>
            <div className="text-right">Suggested</div>
            <div />
          </div>
          <div className="divide-y divide-palette-slate-muted">
            {rows.map((row) => (
              <div
                key={row.clinic}
                className={`grid ${gridCols} items-center gap-2 px-3 py-2 text-sm`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-palette-slate-primary">{row.clinic}</div>
                  {row.clinic_name && (
                    <div className="truncate text-xs text-palette-slate-secondary">
                      {row.clinic_name}
                    </div>
                  )}
                </div>
                <div className="text-right text-palette-slate-heading">{row.total_pdfs}</div>
                <div className="text-right text-palette-slate-heading">{row.credit_notes}</div>
                <div className="text-right text-palette-emerald-hover">{row.matched}</div>
                <div className="text-right text-palette-rose-heading">{row.needs_review}</div>
                <div className="text-right text-palette-slate-heading">
                  {formatMoney(row.amount_total ?? 0)}
                </div>
                <div className="text-right text-palette-emerald-hover">
                  {formatMoney(row.suggested_total ?? 0)}
                </div>
                <div className="text-right">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onReviewClinic(row.clinic)}
                  >
                    Review
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceCard({ invoice, isOpen, onTogglePdf, canDelete, isDeleting, onDelete }) {
  const needsReview = Boolean(invoice.review_reason) || invoice.read_status === "unreadable";
  const isCredit = invoice.document_type === "credit_note";
  const amount = invoice.total_amount;

  return (
    <div
      className={`rounded-md border p-3 ${
        isOpen
          ? "border-palette-indigo-ring ring-1 ring-palette-indigo-hover"
          : needsReview
            ? "border-palette-amber-border"
            : "border-palette-slate-divider"
      } bg-white`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-palette-slate-primary">
              {invoice.original_filename}
            </span>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                needsReview
                  ? "bg-palette-amber-muted text-palette-amber-text"
                  : invoice.is_service_charge
                    ? "bg-palette-yellow-muted text-palette-yellow-text"
                    : "bg-palette-emerald-muted text-palette-emerald-text"
              }`}
            >
              {invoice.read_status === "unreadable"
                ? "Unreadable"
                : needsReview
                  ? "Needs review"
                  : invoice.is_service_charge
                    ? "Service charge"
                    : "Matched"}
            </span>
            {invoice.document_type && (
              <span
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  isCredit
                    ? "bg-palette-rose-muted text-palette-rose-text"
                    : "bg-palette-slate-muted text-palette-slate-heading"
                }`}
              >
                {DOCUMENT_TYPE_LABELS[invoice.document_type] || invoice.document_type}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-palette-slate-secondary">
            Clinic:{" "}
            <span className="font-medium text-palette-slate-heading">
              {invoice.clinic}
              {invoice.clinic_name ? ` — ${invoice.clinic_name}` : ""}
            </span>{" "}
            · {invoice.date}
            {invoice.invoice_number ? ` · #${invoice.invoice_number}` : ""}
          </div>
          {/* No doctor on the invoice means a clinic service charge: there is
              nobody to match, so don't show it as an unmatched practitioner. */}
          {invoice.is_service_charge ? (
            <div className="mt-1 text-xs font-medium text-palette-yellow-heading">
              Service charge
            </div>
          ) : (
            <div className="mt-1 text-xs text-palette-slate-secondary">
              Doctor on invoice:{" "}
              <span className="text-palette-slate-heading">{invoice.align_doctor_name || "—"}</span>
              {" · "}Practitioner:{" "}
              {invoice.practitioner_name ? (
                <span className="font-medium text-palette-slate-heading">
                  {invoice.practitioner_name}
                </span>
              ) : (
                <span className="font-medium text-palette-amber-heading">Unmatched</span>
              )}
            </div>
          )}
          {invoice.review_reason && (
            <div className="mt-1">
              <span className="rounded bg-palette-amber-soft px-1.5 py-0.5 text-[11px] font-medium text-palette-amber-heading ring-1 ring-inset ring-palette-amber-border">
                {invoice.review_reason}
              </span>
            </div>
          )}
          <div className="mt-1 text-xs text-palette-slate-secondary">
            Uploaded by {invoice.uploaded_by_name || "unknown"}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="text-sm font-semibold text-palette-slate-primary">
            {amount !== null && amount !== undefined ? formatMoney(amount) : "No amount"}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={onTogglePdf}>
              {isOpen ? "Hide PDF" : "View PDF"}
            </Button>
            {canDelete && (
              <Button type="button" variant="danger" disabled={isDeleting} onClick={onDelete}>
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * `clinicId` locks the panel to one clinic (the clinic page's Lab/Invisalign
 * Invoices tab): no clinic overview, no clinic picker, and the month starts at
 * `defaultMonth` (YYYY-MM). Without it, the Admin page's all-clinic view.
 */
export default function InvisalignInvoiceReviewPanel({
  canAssign = false,
  clinicId = "",
  defaultMonth = "",
}) {
  const isClinicLocked = Boolean(clinicId);
  const [activeTab, setActiveTab] = useState("review");
  const [page, setPage] = useState(1);
  const [documentType, setDocumentType] = useState("");
  const [clinicFilter, setClinicFilter] = useState(clinicId);
  const [month, setMonth] = useState(defaultMonth);
  const [needsReview, setNeedsReview] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [overviewMonth, setOverviewMonth] = useState("");
  const [openInvoiceId, setOpenInvoiceId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingIds, setDeletingIds] = useState([]);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [documentType, clinicFilter, month, needsReview, search]);

  const filters = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      document_type: documentType || undefined,
      clinic: clinicFilter || undefined,
      month: month || undefined,
      needs_review: needsReview ? true : undefined,
      q: search || undefined,
    }),
    [page, documentType, clinicFilter, month, needsReview, search],
  );

  const { data, isLoading, isError, error } = useInvisalignInvoiceReview(filters, {
    enabled: activeTab === "review",
  });
  const overviewFilters = useMemo(() => ({ month: overviewMonth || undefined }), [overviewMonth]);
  const {
    data: overviewData,
    isLoading: overviewLoading,
    isError: overviewIsError,
    error: overviewError,
  } = useInvisalignInvoiceClinicOverview(overviewFilters, { enabled: activeTab === "overview" });
  const deleteInvoice = useDeleteInvisalignInvoice();
  const { data: clinicsData } = useClinics({}, { enabled: !isClinicLocked });

  const invoices = data?.results || [];
  const totalCount = data?.count ?? 0;
  const totalPages = data?.total_pages ?? 1;
  const currentPage = data?.page ?? page;
  const clinics = useMemo(() => {
    const list = clinicsData?.results ?? clinicsData ?? [];
    return [...list]
      .filter((clinic) => clinic && clinic.clinic)
      .sort((a, b) => String(a.clinic).localeCompare(String(b.clinic)));
  }, [clinicsData]);

  async function confirmDelete() {
    if (!deleteConfirm) return;
    const { id, clinic, date } = deleteConfirm;
    setDeleteConfirm(null);
    setDeletingIds((current) => (current.includes(id) ? current : [...current, id]));
    try {
      await deleteInvoice.mutateAsync({ fileId: id, clinic, date });
      if (openInvoiceId === id) setOpenInvoiceId(null);
      showSuccess("Invisalign invoice deleted.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to delete Invisalign invoice."));
    } finally {
      setDeletingIds((current) => current.filter((item) => item !== id));
    }
  }

  function reviewClinic(clinic) {
    setClinicFilter(clinic);
    setMonth(overviewMonth);
    setNeedsReview(false);
    setActiveTab("review");
  }

  // Keep the lock in step when the clinic page switches clinic.
  useEffect(() => {
    if (isClinicLocked) setClinicFilter(clinicId);
  }, [isClinicLocked, clinicId]);

  const hasFilters =
    searchInput || documentType || (!isClinicLocked && clinicFilter) || month || needsReview;

  return (
    <section className="mb-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-palette-slate-primary">
            Invisalign Invoice Review
          </h3>
          <p className="text-xs text-palette-slate-secondary">
            {isClinicLocked ? `${clinicId} Invisalign invoice PDFs` : "All Invisalign invoice PDFs"}
            {totalCount > 0 ? ` · ${totalCount} total` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {((activeTab === "review" && isLoading) ||
            (activeTab === "overview" && overviewLoading)) && (
            <span className="text-xs text-palette-slate-secondary">Loading...</span>
          )}
          {activeTab === "review" && totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage(Math.max(1, currentPage - 1))}
              >
                Prev
              </Button>
              <span className="text-xs text-palette-slate-secondary">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      {!isClinicLocked && (
        <div className="mb-3 flex gap-1 border-b border-palette-slate-divider">
          {[
            { key: "overview", label: "Clinic overview" },
            { key: "review", label: "Review" },
          ].map((tabOption) => (
            <button
              key={tabOption.key}
              type="button"
              onClick={() => setActiveTab(tabOption.key)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium ${
                activeTab === tabOption.key
                  ? "border-palette-indigo-label text-palette-indigo-badge"
                  : "border-transparent text-palette-slate-secondary hover:text-palette-slate-heading"
              }`}
            >
              {tabOption.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === "overview" && (
        <ClinicOverviewTab
          data={overviewData}
          isLoading={overviewLoading}
          isError={overviewIsError}
          error={overviewError}
          onReviewClinic={reviewClinic}
          month={overviewMonth}
          onMonthChange={setOverviewMonth}
        />
      )}

      {activeTab === "review" && (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-3 rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
            <label className="text-xs font-medium text-palette-slate-badge">
              Search
              <input
                type="text"
                placeholder="filename / invoice # / doctor"
                className="mt-1 block w-56 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </label>
            <label className="text-xs font-medium text-palette-slate-badge">
              Type
              <select
                className="mt-1 block w-36 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
                value={documentType}
                onChange={(event) => setDocumentType(event.target.value)}
              >
                <option value="">All types</option>
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {!isClinicLocked && (
              <label className="text-xs font-medium text-palette-slate-badge">
                Clinic
                <select
                  className="mt-1 block w-44 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
                  value={clinicFilter}
                  onChange={(event) => setClinicFilter(event.target.value)}
                >
                  <option value="">All clinics</option>
                  {clinics.map((clinic) => (
                    <option key={clinic.clinic} value={clinic.clinic}>
                      {clinic.clinic}
                      {clinic.name ? ` — ${clinic.name}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <MonthFilter month={month} onMonthChange={setMonth} />
            <label className="flex items-center gap-1.5 text-xs font-medium text-palette-slate-badge">
              <input
                type="checkbox"
                className="rounded border-palette-slate-ring"
                checked={needsReview}
                onChange={(event) => setNeedsReview(event.target.checked)}
              />
              Needs review only
            </label>
            {hasFilters && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearchInput("");
                  setDocumentType("");
                  if (!isClinicLocked) setClinicFilter("");
                  setMonth("");
                  setNeedsReview(false);
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {isError && (
            <div className="text-sm text-palette-rose-badge">
              {getApiErrorMessage(error, "Unable to load Invisalign invoices.")}
            </div>
          )}

          {!isLoading && !isError && invoices.length === 0 && (
            <div className="text-sm text-palette-slate-secondary">
              No Invisalign invoices found.
            </div>
          )}

          {invoices.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <InvoiceCard
                    key={invoice.id}
                    invoice={invoice}
                    isOpen={openInvoiceId === invoice.id}
                    onTogglePdf={() =>
                      setOpenInvoiceId(openInvoiceId === invoice.id ? null : invoice.id)
                    }
                    canDelete={canAssign && invoice.can_delete}
                    isDeleting={deletingIds.includes(invoice.id)}
                    onDelete={() =>
                      setDeleteConfirm({
                        id: invoice.id,
                        clinic: invoice.clinic,
                        date: invoice.date,
                        filename: invoice.original_filename || "",
                      })
                    }
                  />
                ))}
              </div>

              <div className="lg:sticky lg:top-4 lg:self-start">
                {openInvoiceId ? (
                  <>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="truncate text-xs font-medium text-palette-slate-badge">
                        {invoices.find((item) => item.id === openInvoiceId)?.original_filename ||
                          "PDF preview"}
                      </span>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setOpenInvoiceId(null)}
                      >
                        Close
                      </Button>
                    </div>
                    <div className="h-[78vh] overflow-hidden rounded border border-palette-slate-divider bg-palette-slate-muted">
                      <LabInvoicePdfFrame
                        attachmentId={openInvoiceId}
                        title="Invisalign invoice PDF"
                        useViewUrl={useInvisalignInvoiceViewUrl}
                        className="min-h-full"
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex h-[78vh] items-center justify-center rounded border border-dashed border-palette-slate-ring bg-white text-sm text-palette-slate-placeholder">
                    Select &ldquo;View PDF&rdquo; to preview an Invisalign invoice here.
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Delete Invisalign invoice?"
        message={
          deleteConfirm?.filename
            ? `Delete "${deleteConfirm.filename}"? The PDF is removed from storage and its amount is no longer suggested.`
            : "Delete this Invisalign invoice? The PDF is removed from storage and its amount is no longer suggested."
        }
        confirmText="Delete"
        confirmVariant="danger"
      />
    </section>
  );
}
